// src/components/routing/TeamMembersModal.js
// FlowSense AI — Enhanced Team Members Management Modal
// v2.0 | Glass Morphism, Framer Motion Animations, Search/Filter, Bulk Add,
//         Role Badges, Stats Bar, Confirmation Dialog, Sort, Performance Optimized
// --- UPDATE (FlowSense v3 · AI Explainer)
//   • Every operational role can be added (was technicians only): inspector, contractor, staff,
//     supervisor … with their own badges; role spellings normalised (Technician → tech).
//   • Skills on each member and a team skill-coverage strip (from users.skills).
//   • FlowSense AI explainer: is the lead actually in the team, are there field crew, which
//     skills the team is missing, contractors mixed with internal staff — plus an AI explanation.
//   • Available users filter by role; ids compared as strings (numeric ids broke the lead star).
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, VStack, HStack, Avatar, Text, Badge, IconButton, useToast, Select,
    Box, Spinner, Flex, Divider, useColorModeValue, Input, InputGroup, InputLeftElement,
    InputRightElement, Tooltip, SimpleGrid, Icon, Tag, TagLabel, TagLeftIcon,
    Collapse, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay, useDisclosure, Skeleton, SkeletonCircle,
    CircularProgress, CircularProgressLabel, Wrap, WrapItem, Checkbox,
    Progress,
} from '@chakra-ui/react';
import {
    DeleteIcon, AddIcon, SearchIcon, CloseIcon, CheckIcon,
} from '@chakra-ui/icons';
import {
    MdPerson, MdPersonAdd, MdPersonRemove, MdGroups, MdStar,
    MdCheckCircle, MdError, MdRefresh, MdBolt, MdShield,
    MdEngineering, MdAdminPanelSettings, MdEmail, MdExpandMore,
    MdSelectAll, MdSort, MdFilterList,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import * as teamService from '../../services/teamService';
import { fetchUsers } from '../../services/userService';
// --- UPDATE (AI Explainer)
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer';
const ROLE_ALIASES = { technician: 'tech', technicians: 'tech', field_tech: 'tech', field_technician: 'tech', fco: 'tech', administrator: 'admin', field_inspector: 'inspector', vendor: 'contractor', subcontractor: 'contractor', help_desk: 'helpdesk' };
const normRole = (r) => { const k = String(r || 'user').trim().toLowerCase().replace(/[\s-]+/g, '_'); return ROLE_ALIASES[k] || k; };
const FIELD_ROLES = ['tech', 'inspector', 'contractor'];
const parseSkills = (raw) => {
    if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
    if (typeof raw === 'string' && raw.trim() && raw.trim() !== '[]') { try { const p = JSON.parse(raw); return Array.isArray(p) ? p.map(String) : []; } catch (_) { return []; } }
    return [];
};
const sameId = (a, b) => a != null && b != null && String(a) === String(b);
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION PRESETS
// ─────────────────────────────────────────────────────────────────────────────
const ANIM = {
    fadeInUp:    { initial:{ opacity:0, y:14 },  animate:{ opacity:1, y:0 } },
    fadeInLeft:  { initial:{ opacity:0, x:-14 }, animate:{ opacity:1, x:0 } },
    scaleIn:     { initial:{ opacity:0, scale:0.92 }, animate:{ opacity:1, scale:1 } },
    slideOut:    { exit:{ opacity:0, x:50, height:0, marginBottom:0, padding:0, overflow:'hidden' } },
    stagger:     (i, base=0.04) => ({ delay: i * base }),
    spring:      { type:'spring', stiffness:300, damping:24 },
    smooth:      { duration:0.28, ease:[0.25, 0.46, 0.45, 0.94] },
};

// ─────────────────────────────────────────────────────────────────────────────
// ROLE CONFIGURATION — colour + icon per role
// ─────────────────────────────────────────────────────────────────────────────
const ROLE_CFG = {
    admin:      { color:'purple', icon:MdAdminPanelSettings, label:'Admin'      },
    manager:    { color:'blue',   icon:MdShield,             label:'Manager'    },
    supervisor: { color:'teal',   icon:MdShield,             label:'Supervisor' },
    tech:       { color:'green',  icon:MdEngineering,        label:'Technician' },
    technician: { color:'green',  icon:MdEngineering,        label:'Technician' },
    // --- UPDATE: FlowSense roles
    inspector:  { color:'orange', icon:MdShield,             label:'Inspector'  },
    contractor: { color:'yellow', icon:MdEngineering,        label:'Contractor' },
    staff:      { color:'cyan',   icon:MdPerson,             label:'Staff'      },
    helpdesk:   { color:'pink',   icon:MdPerson,             label:'Helpdesk'   },
    // --- END UPDATE
    user:       { color:'gray',   icon:MdPerson,             label:'User'       },
};
const getRoleCfg = (role) => ROLE_CFG[normRole(role)] || ROLE_CFG.user; // --- UPDATE: normalised

// ─────────────────────────────────────────────────────────────────────────────
// SMALL SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Animated live-status dot */
const PulseIndicator = ({ color='green', size=2 }) => (
    <Box position="relative" display="inline-flex"
        w={`${size*4}px`} h={`${size*4}px`} flexShrink={0}>
        <MotionBox position="absolute" inset={0} borderRadius="full" bg={`${color}.400`}
            animate={{ scale:[1,1.8,1], opacity:[0.7,0,0.7] }}
            transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }} />
        <Box position="absolute" inset={0} borderRadius="full" bg={`${color}.500`} />
    </Box>
);

/** Skeleton row for loading state */
const MemberSkeleton = () => {
    const skBg = useColorModeValue('gray.100','gray.700');
    return (
        <HStack spacing={3} p={3} borderRadius="xl">
            <SkeletonCircle size="40px" startColor={skBg} />
            <VStack align="start" spacing={1} flex={1}>
                <Skeleton h="14px" w="120px" borderRadius="md" startColor={skBg} />
                <Skeleton h="10px" w="170px" borderRadius="md" startColor={skBg} />
            </VStack>
            <Skeleton h="22px" w="64px" borderRadius="full" startColor={skBg} />
        </HStack>
    );
};

/** Animated empty-state placeholder */
const EmptyState = memo(({ icon:IconComp, title, subtitle }) => (
    <Flex direction="column" align="center" justify="center" py={8} gap={2}>
        <MotionBox animate={{ y:[0,-5,0] }}
            transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}>
            <Icon as={IconComp} boxSize={12} color="gray.300" />
        </MotionBox>
        <Text fontWeight="700" color="gray.400" fontSize="sm">{title}</Text>
        {subtitle && (
            <Text fontSize="xs" color="gray.400" textAlign="center" maxW="250px">{subtitle}</Text>
        )}
    </Flex>
));

// ─────────────────────────────────────────────────────────────────────────────
// TEAM STATS BAR — composition overview with role breakdown
// ─────────────────────────────────────────────────────────────────────────────
const TeamStatsBar = memo(({ members, teamLeadId }) => {
    const statBg      = useColorModeValue('gray.50','gray.700');
    const statBorder  = useColorModeValue('gray.200','gray.600');
    const mutedColor  = useColorModeValue('gray.500','gray.400');

    const stats = useMemo(() => {
        const roleMap = {};
        let leadFound = false;
        (members || []).forEach(m => {
            const r = normRole(m.role); // --- UPDATE
            roleMap[r] = (roleMap[r] || 0) + 1;
            if (sameId(m.id, teamLeadId)) leadFound = true; // --- UPDATE
        });
        return { total: members.length, roles: roleMap, hasLead: leadFound };
    }, [members, teamLeadId]);

    if (stats.total === 0) return null;

    return (
        <MotionBox {...ANIM.fadeInUp} transition={ANIM.smooth}>
            <Box p={3} bg={statBg} borderRadius="xl" borderWidth="1px" borderColor={statBorder}>
                <HStack justify="space-between" mb={2}>
                    <HStack spacing={2}>
                        <Icon as={MdGroups} color="blue.400" boxSize={4} />
                        <Text fontSize="2xs" fontWeight="700" color={mutedColor}
                            textTransform="uppercase" letterSpacing="wider">
                            Team Composition
                        </Text>
                    </HStack>
                    {stats.hasLead && (
                        <Tag size="sm" colorScheme="green" borderRadius="full" variant="subtle">
                            <TagLeftIcon as={MdStar} boxSize={3} />
                            <TagLabel fontSize="2xs">Lead Assigned</TagLabel>
                        </Tag>
                    )}
                </HStack>
                <Wrap spacing={2}>
                    <WrapItem>
                        <Tag size="sm" colorScheme="blue" borderRadius="full" variant="subtle">
                            <TagLabel fontSize="2xs" fontWeight="700">{stats.total} Total</TagLabel>
                        </Tag>
                    </WrapItem>
                    {Object.entries(stats.roles).map(([role, count]) => {
                        const rc = getRoleCfg(role);
                        return (
                            <WrapItem key={role}>
                                <Tag size="sm" colorScheme={rc.color} borderRadius="full" variant="outline">
                                    <TagLeftIcon as={rc.icon} boxSize={3} />
                                    <TagLabel fontSize="2xs">
                                        {count} {rc.label}{count > 1 ? 's' : ''}
                                    </TagLabel>
                                </Tag>
                            </WrapItem>
                        );
                    })}
                </Wrap>
            </Box>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MEMBER ROW — single member display with animated hover + role badge
// ─────────────────────────────────────────────────────────────────────────────
const MemberRow = memo(({ member, index, teamLeadId, isSubmitting, onRemove }) => {
    const borderColor = useColorModeValue('gray.200','gray.600');
    const hoverBg     = useColorModeValue('gray.50','gray.700');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const cardBg      = useColorModeValue('white','gray.800');

    const isLead = sameId(member.id, teamLeadId); // --- UPDATE: string-safe
    const skills = parseSkills(member.skills); // --- UPDATE
    const rc     = getRoleCfg(member.role);
    const name   = member.username
        || `${member.first_name || ''} ${member.last_name || ''}`.trim()
        || 'Unknown';

    return (
        <MotionBox
            {...ANIM.fadeInLeft}
            transition={{ ...ANIM.stagger(index, 0.05), ...ANIM.smooth }}
            {...ANIM.slideOut}
            layout
        >
            <Flex
                p={3} borderWidth="1px"
                borderColor={isLead ? 'green.300' : borderColor}
                borderRadius="xl" align="center" justify="space-between"
                bg={cardBg}
                borderLeftWidth={isLead ? '3px' : '1px'}
                borderLeftColor={isLead ? 'green.400' : borderColor}
                _hover={{
                    bg:hoverBg, borderColor: isLead ? 'green.400' : 'blue.200',
                    transform:'translateX(3px)', boxShadow:'sm',
                }}
                transition="all 0.2s" role="group"
            >
                <HStack spacing={3} flex={1} minW={0}>
                    {/* Avatar with lead star badge */}
                    <Box position="relative" flexShrink={0}>
                        <Avatar size="sm" name={name}
                            bg={isLead ? 'green.500' : `${rc.color}.500`} color="white" />
                        {isLead && (
                            <MotionBox
                                position="absolute" top="-2px" right="-2px"
                                animate={{ scale:[1,1.2,1] }}
                                transition={{ duration:2, repeat:Infinity }}
                            >
                                <Box bg="yellow.400" borderRadius="full" p={0.5}
                                    boxShadow="0 0 6px var(--chakra-colors-yellow-400)">
                                    <Icon as={MdStar} boxSize={2.5} color="white" />
                                </Box>
                            </MotionBox>
                        )}
                    </Box>
                    <VStack align="start" spacing={0} minW={0} flex={1}>
                        <HStack spacing={1.5}>
                            <Text fontWeight="700" fontSize="sm" noOfLines={1}>{name}</Text>
                            {isLead && (
                                <Badge colorScheme="green" fontSize="2xs" borderRadius="full"
                                    variant="subtle">Lead</Badge>
                            )}
                        </HStack>
                        {member.email && (
                            <HStack spacing={0.5}>
                                <Icon as={MdEmail} boxSize={2.5} color={mutedColor} />
                                <Text fontSize="2xs" color={mutedColor} noOfLines={1}>
                                    {member.email}
                                </Text>
                            </HStack>
                        )}
                        {!member.email && (
                            <Text fontSize="2xs" color={mutedColor}>No email</Text>
                        )}
                        {/* --- UPDATE: skills */}
                        {skills.length > 0 && (
                            <HStack spacing={1} mt={1} flexWrap="wrap">
                                {skills.slice(0, 3).map(s => (
                                    <Tag key={s} size="sm" variant="subtle" colorScheme="teal" borderRadius="full"><TagLabel fontSize="2xs">{s.replace(/_/g, ' ')}</TagLabel></Tag>
                                ))}
                                {skills.length > 3 && <Text fontSize="2xs" color={mutedColor}>+{skills.length - 3}</Text>}
                            </HStack>
                        )}
                    </VStack>
                </HStack>

                <HStack spacing={2} flexShrink={0}>
                    <Badge colorScheme={rc.color} fontSize="2xs" borderRadius="full"
                        variant="subtle" px={2} py={0.5}>
                        <HStack spacing={1}>
                            <Icon as={rc.icon} boxSize={3} />
                            <Text>{rc.label}</Text>
                        </HStack>
                    </Badge>
                    <Tooltip label={`Remove ${name}`} hasArrow placement="left">
                        <IconButton
                            icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost"
                            aria-label="Remove member" isLoading={isSubmitting} borderRadius="full"
                            opacity={0} _groupHover={{ opacity:1 }} transition="all 0.2s"
                            _hover={{ bg:'red.50', transform:'scale(1.1)' }}
                            onClick={() => onRemove(member.id, name)}
                        />
                    </Tooltip>
                </HStack>
            </Flex>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// AVAILABLE USER ROW — checkbox + avatar in the "Add" panel
// ─────────────────────────────────────────────────────────────────────────────
const UserOptionRow = memo(({ user, isSelected, onToggle }) => {
    const hoverBg    = useColorModeValue('blue.50','blue.900');
    const selBg      = useColorModeValue('blue.50','blue.900');
    const mutedColor = useColorModeValue('gray.500','gray.400');
    const rc = getRoleCfg(user.role);
    const name = user.username
        || `${user.first_name || ''} ${user.last_name || ''}`.trim()
        || 'Unknown';

    return (
        <MotionBox whileHover={{ x:2 }} transition={{ duration:0.15 }}>
            <Flex
                p={2.5} borderRadius="lg" cursor="pointer"
                bg={isSelected ? selBg : 'transparent'}
                borderWidth={isSelected ? '1px' : '0px'}
                borderColor={isSelected ? 'blue.300' : 'transparent'}
                _hover={{ bg:hoverBg }}
                onClick={() => onToggle(user.id)}
                transition="all 0.15s" align="center" gap={2}
            >
                <Checkbox isChecked={isSelected} colorScheme="blue" size="sm"
                    onChange={() => onToggle(user.id)} pointerEvents="none" />
                <Avatar size="xs" name={name} bg={`${rc.color}.500`} color="white" />
                <VStack align="start" spacing={0} flex={1} minW={0}>
                    <Text fontSize="xs" fontWeight="600" noOfLines={1}>{name}</Text>
                    <Text fontSize="2xs" color={mutedColor} noOfLines={1}>
                        {user.email || 'No email'}
                    </Text>
                </VStack>
                <Badge colorScheme={rc.color} fontSize="2xs" borderRadius="full" variant="subtle">
                    {rc.label}
                </Badge>
            </Flex>
        </MotionBox>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MODAL COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const TeamMembersModal = ({ isOpen, onClose, currentTeam, onUpdate }) => {
    // ── State ────────────────────────────────────────────────────────────────
    const [members,         setMembers]         = useState([]);
    const [availableUsers,  setAvailableUsers]  = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [isLoading,       setIsLoading]       = useState(false);
    const [isSubmitting,    setIsSubmitting]    = useState(false);
    const [searchMembers,   setSearchMembers]   = useState('');
    const [searchAvailable, setSearchAvailable] = useState('');
    const [sortBy,          setSortBy]          = useState('name'); // 'name' | 'role'
    const [showAddPanel,    setShowAddPanel]    = useState(true);
    const [removeTarget,    setRemoveTarget]    = useState(null);
    const [recentAction,    setRecentAction]    = useState(null);
    const [roleFilter,      setRoleFilter]      = useState('all');
    const [availRole,       setAvailRole]       = useState('field'); // --- UPDATE: field | all | <role>

    // ── Hooks — all unconditional, top-level ─────────────────────────────────
    const toast    = useToast();
    const cancelRef = useRef(null);
    const {
        isOpen: isAlertOpen,
        onOpen: onAlertOpen,
        onClose: onAlertClose,
    } = useDisclosure();

    // ── Theme tokens ─────────────────────────────────────────────────────────
    const modalBg        = useColorModeValue('white','gray.800');
    const headerBg       = useColorModeValue('gray.50','gray.750');
    const rowBg          = useColorModeValue('white','gray.800');
    const borderColor    = useColorModeValue('gray.200','gray.600');
    const addSectionBg   = useColorModeValue('blue.50','blue.900');
    const searchBg       = useColorModeValue('white','gray.700');
    const mutedColor     = useColorModeValue('gray.500','gray.400');
    const accentGradient = useColorModeValue(
        'linear(to-r, blue.500, purple.500)',
        'linear(to-r, blue.400, purple.400)'
    );
    const addPanelLabel  = useColorModeValue('blue.700','blue.200');

    // ── Custom scrollbar CSS ─────────────────────────────────────────────────
    const scrollCSS = useMemo(() => ({
        '&::-webkit-scrollbar':       { width:'4px' },
        '&::-webkit-scrollbar-track': { background:'transparent' },
        '&::-webkit-scrollbar-thumb': { background:'var(--chakra-colors-gray-300)', borderRadius:'2px' },
    }), []);

    // ── Data fetching (original logic preserved) ─────────────────────────────
    const fetchData = useCallback(async () => {
        if (!currentTeam?.id) return;

        setIsLoading(true);
        try {
            // 1. Fetch current team members
            const currentMembers = await teamService.getUsersInTeam(currentTeam.id);
            setMembers(currentMembers || []);

            // 2. Fetch all eligible users using your existing userService
            // --- UPDATE: every operational role (was role: 'tech' only); inactive and basic 'user' accounts left out
            const response = await fetchUsers({ limit: 500 });
            const allUsers = (Array.isArray(response?.data) ? response.data : [])
                .filter(u => normRole(u.role) !== 'user' && (!u.status || u.status === 'active'));
            // --- END UPDATE

            // Filter out users already in this team
            const currentMemberIds = new Set((currentMembers || []).map(m => String(m.id))); // --- UPDATE: string-safe
            const available = allUsers.filter(u => !currentMemberIds.has(String(u.id)));

            setAvailableUsers(available);
        } catch (err) {
            toast({
                title: 'Error loading data',
                description: err.message || 'Could not load team members.',
                status: 'error', duration: 3000, isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    }, [currentTeam, toast]);

    useEffect(() => {
        if (isOpen) {
            fetchData();
            setSelectedUserIds(new Set());
            setSearchMembers('');
            setSearchAvailable('');
            setRecentAction(null);
            setRoleFilter('all');
        }
    }, [isOpen, fetchData]);

    // ── Derived / filtered data ──────────────────────────────────────────────
    const filteredMembers = useMemo(() => {
        let list = members;

        // Text search
        if (searchMembers.trim()) {
            const q = searchMembers.toLowerCase();
            list = list.filter(m =>
                (m.username || '').toLowerCase().includes(q) ||
                (m.first_name || '').toLowerCase().includes(q) ||
                (m.last_name || '').toLowerCase().includes(q) ||
                (m.email || '').toLowerCase().includes(q)
            );
        }

        // Role filter
        if (roleFilter !== 'all') {
            list = list.filter(m => normRole(m.role) === roleFilter); // --- UPDATE
        }

        // Sort
        return [...list].sort((a, b) => {
            if (sortBy === 'role') return (a.role || '').localeCompare(b.role || '');
            const nA = a.username || `${a.first_name || ''} ${a.last_name || ''}`;
            const nB = b.username || `${b.first_name || ''} ${b.last_name || ''}`;
            return nA.localeCompare(nB);
        });
    }, [members, searchMembers, sortBy, roleFilter]);

    const filteredAvailable = useMemo(() => {
        // --- UPDATE: role filter for the add list
        const byRole = availableUsers.filter(u => availRole === 'all' ? true : availRole === 'field' ? FIELD_ROLES.includes(normRole(u.role)) : normRole(u.role) === availRole);
        if (!searchAvailable.trim()) return byRole;
        const q = searchAvailable.toLowerCase();
        return byRole.filter(u =>
            (u.username || '').toLowerCase().includes(q) ||
            (u.first_name || '').toLowerCase().includes(q) ||
            (u.last_name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        );
    }, [availableUsers, searchAvailable, availRole]);

    // --- UPDATE: team skill coverage + explainer
    const teamSkills = useMemo(() => {
        const counts = {};
        members.forEach(m => parseSkills(m.skills).forEach(s => { counts[s] = (counts[s] || 0) + 1; }));
        return Object.entries(counts).sort((a, b) => b[1] - a[1]);
    }, [members]);

    const explainer = useMemo(() => {
        const findings = [];
        const leadId = currentTeam?.team_lead_id;
        const roles = {};
        members.forEach(m => { const r = normRole(m.role); roles[r] = (roles[r] || 0) + 1; });
        const field = FIELD_ROLES.reduce((n, r) => n + (roles[r] || 0), 0);
        if (!members.length) findings.push({ severity: 'high', text: 'This team has no members — any work routed to it reaches nobody.' });
        if (leadId && members.length && !members.some(m => sameId(m.id, leadId))) findings.push({ severity: 'medium', text: `The team lead (${currentTeam?.team_lead_username || 'set on the team'}) is not a member of the team.` });
        if (!leadId) findings.push({ severity: 'medium', text: 'No team lead is set.' });
        if (members.length && !field) findings.push({ severity: 'medium', text: 'No technicians, inspectors or contractors in this team, so it cannot do field work.' });
        if (roles.contractor && (roles.tech || roles.inspector)) findings.push({ severity: 'info', text: 'Contractors and internal staff are mixed — contractors only see work assigned to them.' });
        const noSkill = members.filter(m => parseSkills(m.skills).length === 0);
        if (noSkill.length) findings.push({ severity: 'info', text: `${noSkill.length} member(s) have no skills recorded.`, evidence: noSkill.slice(0, 5).map(m => m.username || m.email).join(', ') });
        if (members.length && !findings.some(x => x.severity !== 'info')) findings.push({ severity: 'ok', text: `${members.length} member(s), ${field} in the field, lead included.` });
        return {
            findings,
            facts: {
                team: currentTeam?.name || null, lead: currentTeam?.team_lead_username || null,
                members: members.slice(0, 40).map(m => ({ name: m.username || `${m.first_name || ''} ${m.last_name || ''}`.trim(), role: normRole(m.role), skills: parseSkills(m.skills) })),
                roles, skill_coverage: teamSkills.slice(0, 20).map(([skill, n]) => ({ skill, people: n })),
                available_to_add: availableUsers.length,
            },
        };
    }, [members, currentTeam, teamSkills, availableUsers.length]);
    // --- END UPDATE

    // Unique roles in current members (for filter chips)
    const memberRoles = useMemo(() => {
        const set = new Set();
        members.forEach(m => set.add(normRole(m.role))); // --- UPDATE
        return Array.from(set);
    }, [members]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const showActionToast = useCallback((type, message) => {
        setRecentAction({ type, message });
        setTimeout(() => setRecentAction(null), 4000);
    }, []);

    const toggleUserSelection = useCallback((userId) => {
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            next.has(userId) ? next.delete(userId) : next.add(userId);
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        if (selectedUserIds.size === filteredAvailable.length && filteredAvailable.length > 0) {
            setSelectedUserIds(new Set());
        } else {
            setSelectedUserIds(new Set(filteredAvailable.map(u => u.id)));
        }
    }, [selectedUserIds, filteredAvailable]);

    // Original add logic — now supports bulk add
    const handleAddMembers = useCallback(async () => {
        if (selectedUserIds.size === 0) return;
        setIsSubmitting(true);
        let ok = 0;
        try {
            for (const userId of selectedUserIds) {
                try {
                    await teamService.addMemberToTeam(currentTeam.id, userId);
                    ok++;
                } catch { /* skip individual failures */ }
            }
            const total = selectedUserIds.size;
            const msg = ok === total
                ? `${ok} member${ok > 1 ? 's' : ''} added successfully`
                : `${ok}/${total} members added`;
            toast({ title:'Members Added', description:msg, status:'success', duration:3000, isClosable:true, position:'top-right' });
            showActionToast('success', msg);
            setSelectedUserIds(new Set());
            setSearchAvailable('');
            await fetchData();
            if (onUpdate) onUpdate();
        } catch (err) {
            toast({ title:'Failed to add members', description:err.message, status:'error', duration:3000, isClosable:true });
            showActionToast('error', 'Failed to add members');
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedUserIds, currentTeam, fetchData, onUpdate, toast, showActionToast]);

    // Original remove logic — now behind confirmation dialog
    const initiateRemove = useCallback((userId, userName) => {
        setRemoveTarget({ id:userId, name:userName });
        onAlertOpen();
    }, [onAlertOpen]);

    const confirmRemove = useCallback(async () => {
        if (!removeTarget) return;
        onAlertClose();
        setIsSubmitting(true);
        try {
            await teamService.removeMemberFromTeam(currentTeam.id, removeTarget.id);
            toast({
                title: 'Member Removed',
                description: `${removeTarget.name} has been removed from the team.`,
                status: 'info', duration: 2000, isClosable: true, position:'top-right',
            });
            showActionToast('info', `${removeTarget.name} removed`);
            await fetchData();
            if (onUpdate) onUpdate();
        } catch (err) {
            toast({ title:'Failed to remove member', description:err.message, status:'error', duration:3000, isClosable:true });
            showActionToast('error', 'Remove failed');
        } finally {
            setIsSubmitting(false);
            setRemoveTarget(null);
        }
    }, [removeTarget, currentTeam, fetchData, onUpdate, toast, onAlertClose, showActionToast]);

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="xl"
                scrollBehavior="inside" motionPreset="slideInBottom">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
                <ModalContent borderRadius="2xl" overflow="hidden" bg={modalBg}
                    boxShadow="0 25px 60px rgba(0,0,0,0.18)">

                    {/* ════════ HEADER ════════ */}
                    <Box position="relative">
                        {/* Accent gradient bar */}
                        <Box position="absolute" top={0} left={0} right={0} h="3px"
                            bgGradient={accentGradient} />

                        <ModalHeader pt={5} pb={3} bg={headerBg}>
                            <HStack spacing={3}>
                                <MotionBox
                                    initial={{ scale:0, rotate:-180 }}
                                    animate={{ scale:1, rotate:0 }}
                                    transition={{ type:'spring', stiffness:200, damping:15 }}
                                >
                                    <Box bgGradient={accentGradient} p={2} borderRadius="xl"
                                        boxShadow="0 4px 14px rgba(66,99,235,0.3)">
                                        <Icon as={MdGroups} color="white" boxSize={5} />
                                    </Box>
                                </MotionBox>
                                <VStack align="start" spacing={0}>
                                    <Text fontWeight="800" fontSize="md">Manage Members</Text>
                                    <HStack spacing={2}>
                                        <Text fontSize="xs" color={mutedColor}>
                                            {currentTeam?.name || 'Team'}
                                        </Text>
                                        <PulseIndicator color="green" size={1.5} />
                                        <Text fontSize="xs" color={mutedColor}>
                                            {members.length} member{members.length !== 1 ? 's' : ''}
                                        </Text>
                                    </HStack>
                                </VStack>
                            </HStack>
                        </ModalHeader>
                        <ModalCloseButton top={4} right={4} borderRadius="full" />
                    </Box>

                    {/* ════════ BODY ════════ */}
                    <ModalBody px={5} py={4}>
                        {isLoading ? (
                            <VStack spacing={3} py={4}>
                                {[1,2,3,4].map(i => <MemberSkeleton key={i} />)}
                            </VStack>
                        ) : (
                            <VStack spacing={5} align="stretch">

                                {/* ── Inline Action Banner ── */}
                                <AnimatePresence>
                                    {recentAction && (
                                        <MotionBox
                                            initial={{ opacity:0, height:0, marginBottom:0 }}
                                            animate={{ opacity:1, height:'auto', marginBottom:0 }}
                                            exit={{ opacity:0, height:0, marginBottom:0 }}
                                            transition={{ duration:0.25 }}
                                        >
                                            <HStack p={3} borderRadius="xl" spacing={2}
                                                bg={recentAction.type === 'success' ? 'green.50'
                                                    : recentAction.type === 'error' ? 'red.50' : 'blue.50'}
                                                borderWidth="1px"
                                                borderColor={recentAction.type === 'success' ? 'green.200'
                                                    : recentAction.type === 'error' ? 'red.200' : 'blue.200'}>
                                                <MotionBox
                                                    initial={{ scale:0 }} animate={{ scale:1 }}
                                                    transition={ANIM.spring}
                                                >
                                                    <Icon
                                                        as={recentAction.type === 'error' ? MdError : MdCheckCircle}
                                                        color={recentAction.type === 'success' ? 'green.500'
                                                            : recentAction.type === 'error' ? 'red.500' : 'blue.500'}
                                                        boxSize={4} />
                                                </MotionBox>
                                                <Text fontSize="xs" fontWeight="600"
                                                    color={recentAction.type === 'success' ? 'green.700'
                                                        : recentAction.type === 'error' ? 'red.700' : 'blue.700'}>
                                                    {recentAction.message}
                                                </Text>
                                            </HStack>
                                        </MotionBox>
                                    )}
                                </AnimatePresence>

                                {/* ── Stats Bar ── */}
                                <TeamStatsBar members={members} teamLeadId={currentTeam?.team_lead_id} />

                                {/* --- UPDATE: team skills + AI explainer */}
                                {teamSkills.length > 0 && (
                                    <Wrap spacing={1.5}>
                                        {teamSkills.slice(0, 10).map(([s, n]) => (
                                            <WrapItem key={s}>
                                                <Tag size="sm" colorScheme="teal" variant="outline" borderRadius="full">
                                                    <TagLabel fontSize="2xs">{s.replace(/_/g, ' ')} · {n}</TagLabel>
                                                </Tag>
                                            </WrapItem>
                                        ))}
                                    </Wrap>
                                )}
                                <FlowSenseAIExplainer
                                    compact
                                    module="teams"
                                    title="Is this team set up to take work?"
                                    facts={explainer.facts}
                                    findings={explainer.findings}
                                    defaultQuestion={`Explain whether ${currentTeam?.name || 'this team'} has the right people and skills, and who I should add.`}
                                    questions={['Who should I add to this team?', 'Which skills is this team missing?']}
                                />
                                {/* --- END UPDATE */}

                                {/* ════════ ADD MEMBERS SECTION ════════ */}
                                <MotionBox {...ANIM.fadeInUp} transition={ANIM.smooth}>
                                    <Box borderRadius="xl" borderWidth="1px" borderColor={borderColor}
                                        overflow="hidden">
                                        {/* Collapsible header */}
                                        <Flex px={4} py={3} bg={addSectionBg} align="center"
                                            justify="space-between" cursor="pointer"
                                            onClick={() => setShowAddPanel(!showAddPanel)}
                                            _hover={{ opacity:0.9 }} transition="all 0.2s">
                                            <HStack spacing={2}>
                                                <MotionBox animate={{ rotate: showAddPanel ? 0 : -90 }}
                                                    transition={{ duration:0.2 }}>
                                                    <Icon as={MdExpandMore} boxSize={5} color="blue.500" />
                                                </MotionBox>
                                                <Icon as={MdPersonAdd} color="blue.500" boxSize={4} />
                                                <Text fontWeight="700" fontSize="sm"
                                                    color={addPanelLabel}>
                                                    Add Members
                                                </Text>
                                            </HStack>
                                            <HStack spacing={2}>
                                                {selectedUserIds.size > 0 && (
                                                    <MotionBox initial={{ scale:0 }} animate={{ scale:1 }}
                                                        transition={ANIM.spring}>
                                                        <Badge colorScheme="blue" borderRadius="full" px={2}
                                                            boxShadow="0 0 8px var(--chakra-colors-blue-200)">
                                                            {selectedUserIds.size} selected
                                                        </Badge>
                                                    </MotionBox>
                                                )}
                                                <Badge colorScheme="gray" borderRadius="full" variant="subtle">
                                                    {availableUsers.length} available
                                                </Badge>
                                            </HStack>
                                        </Flex>

                                        <Collapse in={showAddPanel} animateOpacity>
                                            <Box p={4} borderTopWidth="1px" borderColor={borderColor}>
                                                {/* Search + Select All */}
                                                <HStack spacing={2} mb={3}>
                                                    <InputGroup size="sm" flex={1}>
                                                        <InputLeftElement pointerEvents="none">
                                                            <SearchIcon color="gray.400" boxSize={3} />
                                                        </InputLeftElement>
                                                        <Input
                                                            placeholder="Search available users…"
                                                            value={searchAvailable}
                                                            onChange={e => setSearchAvailable(e.target.value)}
                                                            bg={searchBg} borderRadius="lg" fontSize="xs"
                                                            _focus={{ borderColor:'blue.400',
                                                                boxShadow:'0 0 0 1px var(--chakra-colors-blue-400)' }}
                                                        />
                                                        {searchAvailable && (
                                                            <InputRightElement cursor="pointer"
                                                                onClick={() => setSearchAvailable('')}>
                                                                <CloseIcon color="gray.400" boxSize={2} />
                                                            </InputRightElement>
                                                        )}
                                                    </InputGroup>
                                                    {/* --- UPDATE: role filter */}
                                                    <Select size="sm" maxW="130px" borderRadius="lg" value={availRole} onChange={e => setAvailRole(e.target.value)}>
                                                        <option value="field">Field crew</option>
                                                        <option value="all">All roles</option>
                                                        <option value="tech">Technicians</option>
                                                        <option value="inspector">Inspectors</option>
                                                        <option value="contractor">Contractors</option>
                                                        <option value="supervisor">Supervisors</option>
                                                        <option value="staff">Staff</option>
                                                    </Select>
                                                    <Tooltip label="Select / deselect all" hasArrow>
                                                        <IconButton
                                                            icon={<Icon as={MdSelectAll} />} size="sm"
                                                            variant="outline" borderRadius="lg"
                                                            onClick={handleSelectAll} aria-label="Select all"
                                                            colorScheme={
                                                                selectedUserIds.size === filteredAvailable.length
                                                                && filteredAvailable.length > 0 ? 'blue' : 'gray'
                                                            }
                                                        />
                                                    </Tooltip>
                                                </HStack>

                                                {/* User list */}
                                                <Box maxH="200px" overflowY="auto" borderRadius="lg"
                                                    borderWidth="1px" borderColor={borderColor} css={scrollCSS}>
                                                    {filteredAvailable.length === 0 ? (
                                                        <EmptyState icon={MdPerson}
                                                            title={searchAvailable ? 'No matches' : 'No users available'}
                                                            subtitle={searchAvailable
                                                                ? 'Try a different search'
                                                                : 'All users are already assigned'} />
                                                    ) : (
                                                        <VStack spacing={0} align="stretch" p={1}>
                                                            {filteredAvailable.map(user => (
                                                                <UserOptionRow key={user.id} user={user}
                                                                    isSelected={selectedUserIds.has(user.id)}
                                                                    onToggle={toggleUserSelection} />
                                                            ))}
                                                        </VStack>
                                                    )}
                                                </Box>

                                                {/* Add button */}
                                                <AnimatePresence>
                                                    {selectedUserIds.size > 0 && (
                                                        <MotionBox
                                                            initial={{ opacity:0, y:8 }}
                                                            animate={{ opacity:1, y:0 }}
                                                            exit={{ opacity:0, y:8 }}
                                                            transition={ANIM.spring} mt={3}
                                                        >
                                                            <Button
                                                                colorScheme="blue" size="sm" w="full"
                                                                leftIcon={<Icon as={selectedUserIds.size > 1 ? MdBolt : AddIcon} />}
                                                                onClick={handleAddMembers}
                                                                isLoading={isSubmitting} loadingText="Adding…"
                                                                borderRadius="lg"
                                                                _hover={{ transform:'translateY(-1px)', boxShadow:'lg' }}
                                                                transition="all 0.2s"
                                                            >
                                                                Add {selectedUserIds.size} Member{selectedUserIds.size > 1 ? 's' : ''}
                                                            </Button>
                                                        </MotionBox>
                                                    )}
                                                </AnimatePresence>
                                            </Box>
                                        </Collapse>
                                    </Box>
                                </MotionBox>

                                <Divider />

                                {/* ════════ CURRENT MEMBERS ════════ */}
                                <Box>
                                    {/* Section header + controls */}
                                    <HStack justify="space-between" mb={3}>
                                        <HStack spacing={2}>
                                            <Icon as={MdGroups} color="blue.400" boxSize={4} />
                                            <Text fontWeight="700" fontSize="sm">Current Members</Text>
                                            <Badge colorScheme="blue" borderRadius="full" variant="subtle">
                                                {filteredMembers.length}
                                                {(searchMembers || roleFilter !== 'all')
                                                    ? ` / ${members.length}` : ''}
                                            </Badge>
                                        </HStack>
                                        <HStack spacing={1}>
                                            <Tooltip label={`Sort by ${sortBy === 'name' ? 'role' : 'name'}`} hasArrow>
                                                <IconButton icon={<Icon as={MdSort} />} size="xs"
                                                    variant="ghost" borderRadius="full" aria-label="Sort"
                                                    onClick={() => setSortBy(s => s === 'name' ? 'role' : 'name')} />
                                            </Tooltip>
                                            <Tooltip label="Refresh" hasArrow>
                                                <IconButton icon={<Icon as={MdRefresh} />} size="xs"
                                                    variant="ghost" borderRadius="full" aria-label="Refresh"
                                                    onClick={fetchData} isLoading={isLoading}
                                                    _hover={{ transform:'rotate(180deg)' }}
                                                    transition="all 0.3s" />
                                            </Tooltip>
                                        </HStack>
                                    </HStack>

                                    {/* Search bar (shown when >4 members) */}
                                    {members.length > 4 && (
                                        <MotionBox {...ANIM.fadeInUp} transition={ANIM.smooth} mb={3}>
                                            <InputGroup size="sm">
                                                <InputLeftElement pointerEvents="none">
                                                    <SearchIcon color="gray.400" boxSize={3} />
                                                </InputLeftElement>
                                                <Input placeholder="Filter members…"
                                                    value={searchMembers}
                                                    onChange={e => setSearchMembers(e.target.value)}
                                                    bg={searchBg} borderRadius="lg" fontSize="xs"
                                                    _focus={{ borderColor:'blue.400',
                                                        boxShadow:'0 0 0 1px var(--chakra-colors-blue-400)' }} />
                                                {searchMembers && (
                                                    <InputRightElement cursor="pointer"
                                                        onClick={() => setSearchMembers('')}>
                                                        <CloseIcon color="gray.400" boxSize={2} />
                                                    </InputRightElement>
                                                )}
                                            </InputGroup>
                                        </MotionBox>
                                    )}

                                    {/* Role filter chips */}
                                    {memberRoles.length > 1 && (
                                        <HStack spacing={1} mb={3} overflowX="auto" pb={1}>
                                            <Button size="xs" borderRadius="full" fontSize="2xs"
                                                variant={roleFilter === 'all' ? 'solid' : 'ghost'}
                                                colorScheme={roleFilter === 'all' ? 'blue' : 'gray'}
                                                onClick={() => setRoleFilter('all')}
                                                _hover={{ transform:'translateY(-1px)' }} transition="all 0.15s">
                                                All ({members.length})
                                            </Button>
                                            {memberRoles.map(role => {
                                                const rc = getRoleCfg(role);
                                                const cnt = members.filter(m => normRole(m.role) === role).length;
                                                return (
                                                    <Button key={role} size="xs" borderRadius="full" fontSize="2xs"
                                                        variant={roleFilter === role ? 'solid' : 'ghost'}
                                                        colorScheme={roleFilter === role ? rc.color : 'gray'}
                                                        onClick={() => setRoleFilter(roleFilter === role ? 'all' : role)}
                                                        leftIcon={<Icon as={rc.icon} boxSize={3} />}
                                                        _hover={{ transform:'translateY(-1px)' }} transition="all 0.15s">
                                                        {rc.label} ({cnt})
                                                    </Button>
                                                );
                                            })}
                                        </HStack>
                                    )}

                                    {/* Member list */}
                                    {members.length === 0 ? (
                                        <EmptyState icon={MdGroups} title="No members yet"
                                            subtitle="Add members using the panel above" />
                                    ) : filteredMembers.length === 0 ? (
                                        <EmptyState icon={MdPerson} title="No matches"
                                            subtitle="Try a different search or filter" />
                                    ) : (
                                        <VStack align="stretch" spacing={2} maxH="320px"
                                            overflowY="auto" pr={1} css={scrollCSS}>
                                            <AnimatePresence>
                                                {filteredMembers.map((member, idx) => (
                                                    <MemberRow key={member.id}
                                                        member={member} index={idx}
                                                        teamLeadId={currentTeam?.team_lead_id}
                                                        isSubmitting={isSubmitting}
                                                        onRemove={initiateRemove} />
                                                ))}
                                            </AnimatePresence>
                                        </VStack>
                                    )}
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>

                    {/* ════════ FOOTER ════════ */}
                    <ModalFooter borderTopWidth="1px" borderColor={borderColor} bg={headerBg} py={3}>
                        <HStack spacing={3} w="full" justify="space-between">
                            <Text fontSize="2xs" color={mutedColor}>
                                FlowSense Team Management
                            </Text>
                            <Button onClick={onClose} borderRadius="lg" size="sm">Done</Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ════════ REMOVE CONFIRMATION DIALOG ════════ */}
            <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef}
                onClose={onAlertClose} motionPreset="slideInBottom">
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent borderRadius="xl">
                        <AlertDialogHeader fontSize="md" fontWeight="700">
                            <HStack spacing={2}>
                                <Icon as={MdPersonRemove} color="red.500" />
                                <Text>Remove Member</Text>
                            </HStack>
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Are you sure you want to remove{' '}
                            <Text as="span" fontWeight="700">{removeTarget?.name}</Text> from{' '}
                            <Text as="span" fontWeight="700">{currentTeam?.name}</Text>?
                            {' '}You can re-add them later.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onAlertClose} size="sm" borderRadius="lg">
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={confirmRemove} ml={3} size="sm"
                                borderRadius="lg" leftIcon={<DeleteIcon />} isLoading={isSubmitting}>
                                Remove
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default TeamMembersModal;